import React, { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import LoadingButton from '@mui/lab/LoadingButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Trash2 } from 'lucide-react';

import { FlexColumn } from 'src/components/flex';
import Select from 'src/components/input/select/Select';
import CreateOrUpdatePropertyModal from 'src/components/create-or-update-property-modal/CreateOrUpdatePropertyModal';
import { PROPERTY_INTEGRATIONS_ANCHOR_ID } from 'src/components/create-or-update-property-modal/use-property-section-navigation';
import type { ITourIntegration } from 'src/services/api/properties/types';
import { useProperties } from 'src/services/api';
import { useProperty, type IPropertyWithIntegrations } from 'src/services/api/properties/use-property';
import { useTourIntegrationMutations } from 'src/services/api/properties/use-tour-integration-mutations';

import styles from 'src/components/create-or-update-property-modal/PropertyFactsSection.module.css';

interface Props {
    property: IPropertyWithIntegrations;
}

const INTEGRATION_PROVIDER_OPTIONS = [
    { value: 'elise_ai', label: 'Elise.ai' },
];

const INTEGRATION_PROVIDER_LABELS: Record<string, string> = {
    elise_ai: 'Elise.ai',
};

interface IntegrationFormRow {
    integrationId: number | null;
    provider: string | null;
    externalId: string;
    originalExternalId: string;
    deleted: boolean;
}

type IntegrationOperation =
    | { type: 'create'; provider: string; externalId: string | null }
    | { type: 'update'; integrationId: number; externalId: string | null }
    | { type: 'delete'; integrationId: number };

function integrationToFormRow(integration: ITourIntegration): IntegrationFormRow {
    const externalId = integration.external_property_id ?? '';
    return {
        integrationId: integration.id,
        provider: integration.provider,
        externalId,
        originalExternalId: externalId,
        deleted: false,
    };
}

function getDefaultRows(property: IPropertyWithIntegrations): IntegrationFormRow[] {
    return (property.integrations?.integrations ?? []).map(integrationToFormRow);
}

export default function PropertyIntegrationsSection({ property }: Props) {
    const { create, update, remove } = useTourIntegrationMutations(property.id);
    const { data: properties = [] } = useProperties();
    const [isEditingParent, setIsEditingParent] = useState(false);

    const isReadOnly = property.parent_property_id != null;
    const sourceProperty = properties.find(
        (candidate) => candidate.id === (
            property.integrations?.source_property_id ?? property.parent_property_id
        )
    );
    const { data: sourcePropertyForEditing } = useProperty(
        isEditingParent ? (sourceProperty?.id ?? null) : null,
        { include_facts: true, include_integrations: true, include_touring_hours: true },
    );

    const defaultRows = useMemo(() => getDefaultRows(property), [property.id, property.integrations]);
    const [rows, setRows] = useState<IntegrationFormRow[]>(defaultRows);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setRows(defaultRows);
        setSaveError(null);
    }, [defaultRows]);

    const visibleRows = rows.filter((row) => !row.deleted);
    const reservedProviders = new Set(
        visibleRows.flatMap((row) => (row.provider ? [row.provider] : []))
    );
    const availableProviderOptions = INTEGRATION_PROVIDER_OPTIONS.filter(
        (option) => !reservedProviders.has(option.value)
    );
    const canAddRow = availableProviderOptions.length > 0 &&
        visibleRows.every((row) => Boolean(row.provider));

    const hasUnsavedChanges = rows.some((row) => {
        if (row.integrationId == null) {
            return !row.deleted;
        }
        return row.deleted || row.externalId !== row.originalExternalId;
    });

    const handleAddRow = () => {
        setSaveError(null);
        setRows((prev) => [
            ...prev,
            { integrationId: null, provider: null, externalId: '', originalExternalId: '', deleted: false },
        ]);
    };

    const handleDeleteRow = (index: number) => {
        setSaveError(null);
        const row = rows[index];
        if (!row) {
            return;
        }
        if (row.integrationId == null) {
            setRows((prev) => prev.filter((_, i) => i !== index));
            return;
        }
        setRows((prev) => prev.map((r, i) => (i === index ? { ...r, deleted: true } : r)));
    };

    const handleProviderChange = (index: number, provider: string) => {
        setRows((prev) => prev.map((r, i) => (i === index ? { ...r, provider } : r)));
    };

    const handleExternalIdChange = (index: number, externalId: string) => {
        setRows((prev) => prev.map((r, i) => (i === index ? { ...r, externalId } : r)));
    };

    const applyChanges = async () => {
        setSaveError(null);

        const operations = rows.flatMap((row): IntegrationOperation[] => {
            if (row.integrationId != null && row.deleted) {
                return [{ type: 'delete', integrationId: row.integrationId }];
            }
            if (row.deleted || !row.provider) {
                return [];
            }
            if (row.integrationId == null) {
                return [{ type: 'create', provider: row.provider, externalId: row.externalId || null }];
            }
            if (row.externalId !== row.originalExternalId) {
                return [{ type: 'update', integrationId: row.integrationId, externalId: row.externalId || null }];
            }
            return [];
        });

        if (operations.length === 0) {
            return true;
        }

        setIsSaving(true);
        try {
            const results = await Promise.allSettled(operations.map((op) => {
                switch (op.type) {
                    case 'create':
                        return create.mutateAsync({ provider: op.provider, externalId: op.externalId });
                    case 'update':
                        return update.mutateAsync({ integrationId: op.integrationId, externalId: op.externalId });
                    case 'delete':
                        return remove.mutateAsync({ integrationId: op.integrationId });
                }
            }));

            const errors = results.flatMap((result) => (result.status === 'rejected' ? [result.reason] : []));
            if (errors.length > 0) {
                setSaveError('Failed to save integration changes');
                return false;
            }

            return true;
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <FlexColumn rowGap={2} id={PROPERTY_INTEGRATIONS_ANCHOR_ID}>
            <div className={styles.header}>
                <Typography fontWeight={500} data-property-section-title>Integrations</Typography>
                {!isReadOnly && hasUnsavedChanges && (
                    <Typography variant="caption" color="text.secondary" data-testid="property-integrations-dirty-indicator">
                        Unsaved changes
                    </Typography>
                )}
            </div>

            {isReadOnly && (
                <Alert severity="info" data-testid="property-integrations-inherited-alert">
                    These integrations are inherited from{' '}
                    {sourceProperty ? (
                        <Button
                            type="button"
                            variant="text"
                            onClick={() => setIsEditingParent(true)}
                            className={`${styles.inheritedParentLink} ellipsisText`}
                            title={sourceProperty.name}
                            data-testid="edit-parent-integrations-link"
                        >
                            {sourceProperty.name}
                        </Button>
                    ) : (
                        'the parent property'
                    )}
                    . Edit them on the parent to change this property&rsquo;s integrations.
                </Alert>
            )}

            {isEditingParent && sourcePropertyForEditing && (
                <CreateOrUpdatePropertyModal
                    opened={isEditingParent}
                    onClose={() => setIsEditingParent(false)}
                    property={sourcePropertyForEditing}
                    initialSection="tourSettings"
                    scrollToAnchorId={PROPERTY_INTEGRATIONS_ANCHOR_ID}
                />
            )}

            {saveError && (
                <Alert severity="error" data-testid="property-integrations-error-alert">
                    {saveError}
                </Alert>
            )}

            {rows.map((row, index) => {
                if (row.deleted) {
                    return null;
                }
                const isNewRow = row.integrationId == null;
                const rowTestId = row.provider ?? `row-${index}`;

                return (
                    <div key={row.integrationId ?? `new-${index}`} className={styles.factRow}>
                        {isNewRow ? (
                            <Select
                                label="Integration"
                                placeholder="Select integration"
                                options={availableProviderOptions}
                                value={row.provider}
                                onChange={(value) => handleProviderChange(index, value)}
                                size="small"
                                fullWidth
                                disabled={isReadOnly}
                                data-testid={`property-integration-provider-${rowTestId}`}
                            />
                        ) : (
                            <Typography variant="body2" color="text.secondary" className={styles.factLabel}>
                                {INTEGRATION_PROVIDER_LABELS[row.provider ?? ''] ?? row.provider}
                            </Typography>
                        )}
                        <TextField
                            label="Building ID"
                            value={row.externalId}
                            onChange={(event) => handleExternalIdChange(index, event.target.value)}
                            size="small"
                            fullWidth
                            disabled={isReadOnly}
                            data-testid={`property-integration-external-id-${rowTestId}`}
                        />
                        <IconButton
                            type="button"
                            size="small"
                            onClick={() => handleDeleteRow(index)}
                            disabled={isReadOnly}
                            data-testid={isNewRow ? `delete-new-integration-${rowTestId}` : `delete-integration-${row.provider}`}
                        >
                            <Trash2 size={16} />
                        </IconButton>
                    </div>
                );
            })}

            {!isReadOnly && (
                <Button
                    type="button"
                    variant="outlined"
                    size="medium"
                    fullWidth
                    onClick={handleAddRow}
                    disabled={!canAddRow}
                    className={styles.addRowButton}
                    data-testid="add-property-integration-row-button"
                >
                    + Add Integration
                </Button>
            )}

            {!isReadOnly && (
                <div className={styles.actions}>
                    <LoadingButton
                        type="button"
                        className={styles.saveButton}
                        variant="contained"
                        size="small"
                        loading={isSaving}
                        disabled={!hasUnsavedChanges}
                        onClick={applyChanges}
                        data-testid="save-property-integrations-button"
                    >
                        Save
                    </LoadingButton>
                </div>
            )}
        </FlexColumn>
    );
}
